<template>
    <div :class="statusBuilder('DatatableLayout', status)">
        <div class="DatatableLayout__background">
            <DatatableHeader
                class="DatatableLayout__header"
                :status="status"
                :items="pagination.items"
                :page-header="pageHeader"
            >
                <template v-slot:header>
                    <slot name="header"></slot>
                </template>
                <template v-slot:actions>
                    <slot name="actions"></slot>
                </template>
                <template v-slot:tableFilters>
                    <slot name="tableFilters"></slot>
                </template>
                <template v-slot:tableInfo>
                    <slot name="tableInfo"></slot>
                </template>
            </DatatableHeader>


            <div
                :class="[
                    'DatatableLayout__container',
                    showSidebar && 'DatatableLayout__container--open'
                ]"
            >
                <!--
                Sets the sort field and sort direction
                @event set-sort
                @property object {sort: '', direction: ''}
            -->
                <!--
                v-model input
                @event input
            -->
                <!--
                Used as an alternative to input if a secondary behavior is necessary
                @event row-select
                @property object row data
            -->
                <RPDatatable
                    :value="value"
                    :hide-id="hideId"
                    :headers="filteredHeaders"
                    :table-data="filteredTableData"
                    :sort-property="sortProperty"
                    :sort="sort"
                    :sort-direction="sortDirection"
                    :status="tableStatus"
                    :show-extra-options="showExtraOptions"
                    :show-action-cell="showActionCell"
                    class="DatatableLayout__table"
                    table-type="page"
                    @set-sort="s => $emit('set-sort', s)"
                    @input="v => $emit('input', v)"
                    @row-select="v => $emit('row-select', v)"
                >
                    <template
                        v-for="header in filteredHeaders"
                        v-slot:[header.key]="{prop, rowData}"
                    >
                        <!--
                        @slot Use header key as slot name to customize the display of data in a datacell
                        @binding {string} prop Property associated with this particular cell
                        @binding {object} rowData All data present in row
                    -->
                        <slot
                            :name="header.key"
                            :prop="prop"
                            :rowData="rowData"
                        >
                            {{ rowData[prop] }}
                        </slot>
                    </template>
                    <template v-slot:actionSlot="{ rowId }">
                        <!--
                        @slot used to display the actions in each row.
                        @binding {object} rowId rowId is included to trigger the proper action
                    -->
                        <slot
                            :rowId="rowId"
                            name="actionSlot"
                        >
                        </slot>
                    </template>
                    <template
                        v-if="showExtraOptions"
                        v-slot:extraOptions="{ rowId }"
                    >
                        <slot
                            :rowId="rowId"
                            name="extraOptions"
                        ></slot>
                    </template>
                    <template v-slot:noResults>
                        <!--
                        @slot Content to display when the table data is empty
                    -->
                        <slot name="noResults">
                        </slot>
                    </template>
                    <template v-slot:pagination>
                        <!--
                        Prompt to navigate to particular page in table data
                        @event navToPage
                        @property {object} page information
                    -->
                        <tfoot>
                            <tr>
                                <td colspan="100%">
                                    <RPPagination
                                        class="DatatableLayout__footer"
                                        :pagination="pagination"
                                        @nav-to-page="p => $emit('navToPage', p)"
                                    />
                                </td>
                            </tr>
                        </tfoot>
                    </template>
                </RPDatatable>
                <Sidebar
                    v-if="showSidebar"
                    :sidebar-header="sidebarHeader"
                    :status="sidebarStatus"
                    class="DatatableLayout__sidebar"
                    @click="e => $emit('close-sidebar', e)"
                >
                    <template v-slot:header>
                        <slot name="sidebarHeaderText"></slot>
                    </template>
                    <template v-slot:actions>
                        <slot name="sidebarActions"></slot>
                    </template>
                    <template v-slot:default>
                        <slot name="sidebar"></slot>
                    </template>
                </Sidebar>
            </div>
        </div>
    </div>
</template>

<script>
import RPDatatable from 'components/Complex/RPDatatable/RPDatatable.vue';
import RPPagination from 'components/Complex/Pagination/RPPagination.vue';
import stat from 'status/index';
import DatatableHeader from './DatatableLayout/DatatableHeader.vue';
import Sidebar from './DatatableLayout/Sidebar.vue';
import statusBuilder from 'status/statusBuilder';

export default {
    name: 'RPDatatableLayout',
    components: {
        RPDatatable,
        RPPagination,
        DatatableHeader,
        Sidebar
    },
    props: {
        /**
        * Shows or hides the sidebar
        */
        showSidebar: {
            type: Boolean,
            default: false,
        },
        /**
        * Sidebar header text
        */
        sidebarHeader: {
            type: String,
            default: ''
        },
        /**
        * Text displayed at top of page
        */
        pageHeader: {
            type: String,
            default: ''
        },
        /**
        * Information for the pagination component
        */
        pagination: {
            type: Object,
            default: () => ({
                pagination: {
                    totalPages: -1,
                    currentPage: -1
                },
                items: {
                    count: -1,
                    total: -1
                }
            }),
        },
        /**
        * When displaying the sidebar the primary table will be compressed to show only the data from this field
        */
        compressedField: {
            type: String,
            default: ''
        },
        /**
        * Component Status for the entire Layout component (see status/index.js for list of statuses)
        */
        status: {
            type: String,
            default: stat.STANDBY,
        },
        /**
        * Component Status for the just the datatable component (see status/index.js for list of statuses)
        */
        tableStatus: {
            type: String,
            default: stat.STANDBY,
        },
        sidebarStatus: {
            type: String,
            default: stat.STANDBY,
        },
        /**
        * See Datatable
        */
        value: {
            type: [Array, String, Number],
            default: () => null,
        },
        /**
        * See Datatable
        */
        hideId: {
            type: Boolean,
            default: false,
        },
        /**
        * See Datatable
        */
        headers: {
            type: Array,
            required: true,
        },
        /**
        * See Datatable
        */
        tableData: {
            type: Array,
            required: true,
        },
        sortProperty: {
            type: Object,
            default: () => ({key: 'sort_by', direction: 'sort_direction'})
        },
        /**
        * See Datatable
        */
        sort: {
            type: String,
            default: '',
        },
        /**
        * See Datatable
        */
        sortDirection: {
            type: String,
            default: 'asc',
            validator: val => ['asc', 'desc'].indexOf(val) !== -1,
        },
        /**
        * See Datatable
        */
        showExtraOptions: {
            type: Boolean,
            default: false,
        },
        /**
        * See Datatable
        */
        showActionCell: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        filteredHeaders(){
            return this.showSidebar
                ? this.headers.filter(header => header.key === this.compressedField)
                    .map(h => {
                        delete h.sortable;
                        return h;
                    })
                : this.headers;
        },
        filteredTableData(){
            return this.showSidebar
                ? this.tableData.map(data => ({
                    id: data.id,
                    [this.compressedField]: data[this.compressedField]}))
                : this.tableData;

        },
    },
    destroyed(){
        document.removeEventListener('click', this.closeSidebar);
    },
    mounted() {
        document.addEventListener('click', this.closeSidebar);
    },
    methods: {
        statusBuilder,
        closeSidebar(e){
            if(!this.$el.contains(e.target)){
                // this.$emit('close-sidebar');
            }
        }
    }
};
</script>
<style lang="scss">
    .DatatableLayout {
        border-radius: 4px;
        padding: 2px;
        &--saving { background-image: $gradient-purple; }
        &--success { background-image: $gradient-positive; }
        &--warning { background-image: $gradient-yellow; }
        &--error { background-image: $gradient-negative; }

        &__background {
            height: inherit;
            width: inherit;
            background: $grey-50;
            border-radius: 4px;
            padding: 1rem;
        }
        &__container {
            overflow-x: auto;
            margin-bottom: 1rem;
            padding-bottom: 15rem;
            &--open {
                display: grid;
                @media(min-width: $max-md-width){
                    grid-template-columns: minmax(15rem, min-content) auto;
                    grid-template-rows: min-content auto;
                    column-gap: 1rem;
                }
                @media(max-width: $max-md-width){
                    grid-template-columns: auto;
                    row-gap: 1rem;
                }
            }
        }
        @media(min-width: $max-md-width){
            &__table{
                grid-column: 1;
                grid-row: 1;
            }
            &__sidebar{
                grid-column: 2;
                grid-row: span 2;
            }
        }
        @media(max-width: $max-md-width){
            &__table{
                grid-column: 1;
                grid-row: 1;
            }
            &__sidebar{
                grid-column: 1;
                grid-row: 2;
            }
        }
        &__footer {
            margin: 1.5rem auto 0 auto;
        }
    }
</style>

<docs>
    Datatable layout commonly used where the datatable is the primary ui element on the page.  Includes a sidebar element that can be opened and closed from the parent.
    ```[import](./examples/RPDatatableLayoutExample.vue)
    ```
</docs>
